/**
 * The sixth live route (#27 / R6.2) — Settings on the family's own data, and the one screen that
 * can save a theme.
 *
 * **Why this container had to exist for a one-column feature.** #495 shipped
 * `users.theme_preference` and put `themePreference` on `GET`/`PUT /api/auth/profile`. The screen
 * already had the control: `Settings.jsx` calls `themeController.setMode(id)`. It still could not
 * save, and wiring a store alone would not have fixed it, because `App.jsx`'s `Surface` calls
 * `enterFixtureMode()` for every path missing from `LIVE_CONTAINERS`. That flag makes
 * `persistWithReceipt` return `null` before a request is ever built, which is why the live page
 * read "Theme preview only — connect profile storage to save this choice". A theme cannot go live
 * while its page is a preview, so the page goes live first.
 *
 * **What the server can actually answer.** `GET /api/auth/profile` returns the caller's own row:
 * `firstName`, `lastName`, `email`, `authProvider`, `hasPassword`, `themePreference`.
 * `fetchTenant()` returns the family's name.
 *
 * **What the recovered screen would SAY if handed nothing.** `settingsFixture.js` is what
 * `<Surface>` passes today, and every line of it is a statement about a family that does not
 * exist: a parent called Sara, "The Silva family", 3 notifications, five children with ages,
 * "sara.silva@gmail.com · verified", "Last changed 4 months ago", and a Privacy Policy accepted on
 * a date nobody accepted anything. This is the `/approvals` "3 of 0" defect family, and every one
 * of those props is overridden below with a true value or with the empty value that HIDES its
 * panel.
 *
 * **Six things are OMITTED rather than invented, and each is omitted for a named reason:**
 *
 * | omitted | why |
 * | --- | --- |
 * | `consentRows`, `reconsentNeeded` | there is no consent route in `server/routes/` at all. Terms and Privacy acceptance state does not exist to read. |
 * | `notifications` | no endpoint carries a count. The fixture's `3` is invented; `0` is the component default and is the honest value. |
 * | `currentAvatar`, `familyAvatars`, `avatarSources` | the old presentational Studio, still held shut (note below). **#577 wired a live one instead**: `avatarStudio` carries `components/AvatarStudio.jsx` for a parent, which replaces that section whole. |
 * | ~~`toggles`, `paydayOptions`~~ | **No longer omitted — #663 wired both.** They are passed below and written through `updateTenant`. This row is kept because the reason it once gave still governs every OTHER omission here: an unwired switch that silently drops a choice is the defect, so a control ships wired or not at all. |
 * | `dangerActions` | "Reset family" and "Delete family" with no handler. A destructive button that does nothing is worse than no button. |
 *
 * **The Avatar Studio is hidden whole, and the first attempt got this wrong.** An earlier version
 * passed `avatarSources={[]}` — correctly, so the "Describe it (AI)" buttons stay away — but still
 * passed a real `currentAvatar` and the family grid. `Settings.jsx:878` opens the section when
 * `current || familyList.length > 0 || sourceList.length > 0`, so ANY ONE of the three is enough,
 * and the section then shipped three untrue things a cross-model review caught: an enabled,
 * tab-focusable **"New look"** button wired to nothing (`Settings.jsx:901` renders
 * `onClick={onNewLook ? … : undefined}`), a gold **"hero feature"** chip on a section with no
 * working feature (`Settings.jsx:884`), and the instruction **"Choose a portrait for your
 * profile."** (`Settings.jsx:679`) pointing at a picker that is not rendered. Those are hardcoded
 * inside the section and cannot be overridden from here, so the only honest option a container has
 * is to leave all three props empty. The cost, taken deliberately: the family avatar grid is gone
 * until avatar creation is wired.
 *
 * **The band sentence is computed from `persistence`, not from this container's own read.** The
 * first version gated it on `status === 'ready'` — this container's `GET /api/auth/profile` — and
 * that is the wrong question, because whether a theme click reaches the account is decided
 * somewhere else entirely: `ActorContext` → `ProfileThemeBinding` → `ThemeController`'s
 * `selectedId`. Those two answers disagree on the ordinary path. `SettingsRoute` is eagerly
 * imported in `App.jsx`, React runs passive effects children-first, and `ActorProvider` is an
 * ANCESTOR, so this container's profile GET is dispatched BEFORE the session verify and normally
 * wins. In that window the old banner said "Your theme choice is saved to your account" while
 * `selectedId` was still null, every click took the device branch, and the radio then snapped back
 * under the user's finger when verify landed. `persistence` is the one value that already knows:
 * `ThemeController.jsx:58` reports `device` whenever no profile is selected.
 *
 * **The vanishing-warning trap**, documented by `OnboardingRoute`, `ApprovalsRoute` and
 * `VillageRoute`: going live leaves fixture mode, which silently removes `FixtureBanner`. This
 * route carries its own sentence in `bandSubtitle` instead.
 */
import React, { Suspense, lazy, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import LanternLoader from '../components/LanternLoader.jsx';
import NotificationSettings from '../components/NotificationSettings.jsx';
import SampleWeekPanel from '../components/SampleWeekPanel.jsx';
import { ApiError, api } from '../api/client.js';
import { fetchFamilyMembers, fetchTenant, updateTenant } from '../api/session.js';
import { useOptionalActor } from '../state/ActorContext.jsx';
import {
  PAYDAY_PERIODS,
  WEEKDAY_NAMES_MONDAY_FIRST,
  dayNameFromPaydayPeriod,
  paydayKind,
  paydayPeriodFromDayName,
} from '../lib/payday.js';
import { CONFIRMATION_REQUIRED, codeOf } from '../api/paydayChange.js';
import { WEEKEND_CHANGE_PENDING, WEEKEND_RULE_PENDING } from '../lib/weekendRule.js';
import PaydayChangePanel, { PAYDAY_CHOICES, usePaydayChange } from '../components/PaydayChangePanel.jsx';
import { useOptionalTheme } from '../state/ThemeController.jsx';

const Settings = lazy(() => import('../pages/Settings.jsx'));
const AvatarStudio = lazy(() => import('../components/AvatarStudio.jsx'));

/**
 * #577 — the Studio's deep link, read from the HASH because this app routes with `HashRouter`.
 * `#/settings?avatarFor=12` opens the Studio on member 12, and Google Photos comes back to the same
 * place with `photosSessionId` and `photosPickerUri` appended (`googlePhotos.routes.js`).
 */
export function studioLinkFromHash(hash = typeof window === 'undefined' ? '' : window.location.hash) {
  const query = new URLSearchParams(String(hash).split('?').slice(1).join('?'));
  const avatarFor = Number(query.get('avatarFor'));
  const sessionId = query.get('photosSessionId');
  const pickerUri = query.get('photosPickerUri');
  return {
    avatarFor: Number.isInteger(avatarFor) && avatarFor > 0 ? avatarFor : null,
    googleReturn: sessionId && /^[a-f0-9]{64}$/.test(sessionId) && pickerUri && /^https:\/\//.test(pickerUri)
      ? { sessionId, pickerUri }
      : null,
    open: query.has('avatarFor') || query.get('studio') === 'open',
  };
}

/** `GET /api/users` rows → the Studio's members. The name rule is the picker's: never a username that is an email. */
export function toStudioMembers(rows) {
  return (Array.isArray(rows) ? rows : [])
    .filter((row) => row && row.id != null)
    .map((row) => ({
      id: row.id,
      name: row.first_name || row.display_name || (String(row.username || '').includes('@') ? 'Family member' : row.username) || 'Family member',
      role: row.role,
      avatar: row.avatar_url || null,
    }));
}

/**
 * The half that is true in every state, so the three sentences below cannot drift apart.
 *
 * **Family settings came OFF this list when #663 wired them** (`onTogglesChange` and
 * `onPaydayChange` below, both writing through `updateTenant`). The sentence was not changed
 * with them, so for one merge every notice told a parent their family settings were not built
 * while the switch they had just flipped was being saved. `SettingsRoute.test.jsx` now asserts
 * the SENTENCE rather than the constant, because the four `bandNoticeFor` tests compare the
 * function's answer to these same constants and pass for any wording, true or false.
 *
 * The two that remain are still true and are checked by the same test: `profileRows` is
 * read-only, and `consentRows` is passed `[]` because no route answers terms/privacy acceptance.
 */
const UNBUILT = 'Editing your profile and your consent records are not built into this screen '
  + 'yet.';

/** The theme reaches the account: a profile is bound and its store is answering. */
export const LIVE_NOTICE = `Your theme choice is saved to your account. ${UNBUILT}`;

/** No profile is bound, so `ThemeController` takes its device branch and writes localStorage. */
export const DEVICE_NOTICE = `Your theme choice is remembered on this device only. ${UNBUILT}`;

/**
 * Said for `unconnected` and `error`, and when there is no controller at all.
 *
 * The Family settings card already renders its own line for both of those
 * (`Settings.jsx:1032-1033`, "Theme preview only — …"). The band must not contradict it in the
 * same render, and it must not repeat it either, so it claims nothing about saving.
 */
export const NOT_SAVING_NOTICE = UNBUILT;

/** @param {string|undefined} persistence `ThemeController`'s own word for where a choice goes. */
export function bandNoticeFor(persistence) {
  if (persistence === 'loading' || persistence === 'saving' || persistence === 'saved') return LIVE_NOTICE;
  if (persistence === 'device') return DEVICE_NOTICE;
  return NOT_SAVING_NOTICE;
}

/** #577: said when the family list the Studio needs could not be read. */
export const STUDIO_UNAVAILABLE = 'We could not load your family, so the Avatar Studio is closed for now. Nothing was changed.';

export const EXPIRED_TITLE = 'Your sign-in has expired';
export const EXPIRED_BODY = 'Please sign in again at #/login. Nothing was changed.';
export const LOAD_FAILED = "We couldn't load your settings just now — nothing was changed. "
  + 'Give it another moment.';

/**
 * A 401 is not a delay, and it is not retryable from here.
 *
 * The generic title is "Settings are taking a moment" (`Settings.jsx:717`), which is a claim about
 * speed sitting over a claim about expiry. And `Settings.jsx:1157` renders "Try again" only when
 * `onRetry` is a function, so withholding the handler is how a container declines to offer a
 * button that re-issues the same request and gets the same 401 forever.
 */
export function errorStateFor(error) {
  if (error instanceof ApiError && error.status === 401) {
    return { title: EXPIRED_TITLE, message: EXPIRED_BODY, retryable: false };
  }
  return { title: undefined, message: LOAD_FAILED, retryable: true };
}

export const SAVE_FORBIDDEN = 'Only a parent can change the family settings — nothing was changed.';
export const SAVE_FAILED = "That setting didn't save — nothing was changed. Try again in a moment.";
export const FAMILY_UNREADABLE = "We couldn't read your family settings, so they aren't shown here. "
  + 'Nothing was changed.';
export const PAYDAY_UNREADABLE = "Your family's payday is stored as something we don't recognise, "
  + "so it isn't shown. Nothing was changed.";

/**
 * The three switches this screen may actually write, and the field each one saves to.
 *
 * `Settings.jsx:254` names the switches by a SHORT id; `PATCH /api/tenants/current` names the
 * columns by a longer one. Writing them out pair by pair, rather than deriving one from the
 * other, is the same discipline `payday.js` applies to weekdays: a screen id and a column name
 * are two vocabularies, and a mapping that guesses will one day write the wrong family setting
 * with nothing thrown and nothing logged.
 *
 * A fourth switch would need a column before it could be listed here. The header's older note
 * about "sounds" and "leaderboard" describes controls that have none.
 */
const TOGGLE_FIELD = Object.freeze({
  approval: 'approvalRequired',
  weekendGrace: 'weekendGrace',
  familyActivity: 'familyActivityEnabled',
});

/**
 * The switch names, for the sentence a settled save leaves behind.
 *
 * These duplicate `Settings.jsx`'s `DEFAULT_TOGGLES` labels, deliberately and narrowly. The
 * alternative is to send the label DOWN and read it back UP, which makes the note depend on
 * whatever a caller passed as `toggles` — and a host that passed no labels would produce
 * "Setting is saved: off", which names nothing. A drift here changes one sentence and is caught
 * by the test that pins it.
 */
const TOGGLE_LABEL = Object.freeze({
  approval: 'Approval required',
  weekendGrace: 'Weekend grace',
  familyActivity: 'Family activity',
});

/**
 * What a SETTLED, successful save says — in the same place, and the same shape, as a failure.
 *
 * It names the value the SERVER confirmed, never the value that was sent. A 200 that kept the old
 * setting is a real answer a parent needs, and this is the sentence that gives it to them.
 */
export function savedNoteFor(id, confirmed) {
  if (typeof confirmed !== 'boolean') return '';
  return `${TOGGLE_LABEL[id] || 'That setting'} is saved: ${confirmed ? 'on' : 'off'}`;
}

/**
 * The tenant's saved answers, as the `{ id: boolean }` map the switches read.
 *
 * **All three fields must be real booleans, or this answers `null`.** The first version accepted
 * any object, so a response carrying only `{ id, name }` — a partial answer, an array, a build
 * that stopped sending a column — became three confident `false` values on screen. A parent would
 * have read "approval off" as their family's saved answer when nothing had been read at all.
 * `null` means "not known", and `null` is what hides the switches. A cross-model reviewer found it.
 */
export function toToggleValues(tenant) {
  if (!tenant || typeof tenant !== 'object' || Array.isArray(tenant)) return null;
  const { approvalRequired, weekendGrace, familyActivityEnabled } = tenant;
  if (typeof approvalRequired !== 'boolean') return null;
  if (typeof weekendGrace !== 'boolean') return null;
  if (typeof familyActivityEnabled !== 'boolean') return null;
  return { approval: approvalRequired, weekendGrace, familyActivity: familyActivityEnabled };
}

/**
 * The weekday name the select should show, or `null` when a weekday is the wrong question.
 *
 * `dayNameFromPaydayPeriod` THROWS for a period the database could not hold, and that throw must
 * not take the settings screen down — a family whose row drifted still needs to read their own
 * name. So an unreadable period is treated exactly like a monthly one: the payday row is not
 * drawn at all, rather than drawn showing a day nobody chose.
 */
export function paydayNameFor(tenant) {
  const period = tenant && typeof tenant === 'object' ? tenant.paydayPeriod : null;
  if (typeof period !== 'string') return { kind: 'unreadable', name: null };
  try {
    // Three real kinds (#580 I8a). Monthly and ad hoc both have no weekday, and `payday.js` is
    // explicit that this is not "unknown": a weekday is the wrong question for these families.
    const kind = paydayKind(period);
    return kind === 'weekly' ? { kind, name: dayNameFromPaydayPeriod(period) } : { kind, name: null };
  } catch {
    // A period the database could not hold. Collapsing this into 'monthly' told a family with a
    // corrupt row nothing at all, and offered no way to notice. It is a different answer and it
    // gets a different one. A cross-model reviewer found it.
    return { kind: 'unreadable', name: null };
  }
}

function saveNoteFor(error) {
  if (error instanceof ApiError && error.status === 403) return SAVE_FORBIDDEN;
  // #583 unit 6: a second weekend change on one day waits for tomorrow, not for "a moment".
  if (codeOf(error) === WEEKEND_RULE_PENDING) return WEEKEND_CHANGE_PENDING;
  return SAVE_FAILED;
}

function displayNameOf(profile) {
  const full = [profile?.firstName, profile?.lastName].filter(Boolean).join(' ').trim();
  return full || profile?.displayName || profile?.username || '';
}

/**
 * The four profile rows, each stating only what the endpoint returned.
 *
 * No row carries an `action`. Changing a name, an email or a password are real endpoints, and
 * none of them has a flow on this screen, so offering the button would be the same broken promise
 * the theme control used to make.
 */
export function toProfileRows(profile) {
  if (!profile) return [];
  const rows = [
    { id: 'name', label: 'Name', value: displayNameOf(profile) || 'Not set' },
    // NOT "· verified". `getUserProfile` returns no verification flag, and the fixture's one is
    // invented.
    { id: 'email', label: 'Email', value: profile.email || 'Not set' },
    {
      id: 'password',
      label: 'Password',
      // NOT "Last changed 4 months ago". No column carries that date.
      value: profile.hasPassword ? 'A password is set' : 'No password set',
    },
  ];
  const google = profile.authProvider === 'google';
  rows.push({
    id: 'google',
    label: 'Google account',
    value: google ? 'You sign in with Google' : 'Not linked',
    ...(google ? { chip: { tone: 'moss', label: 'Google linked', google: true } } : {}),
  });
  return rows;
}

export default function SettingsRoute() {
  const [state, setState] = useState({
    status: 'loading', profile: null, tenant: null, tenantFailed: false, error: null,
  });
  const [reloadKey, setReloadKey] = useState(0);
  const persistence = useOptionalTheme()?.persistence;

  // #577 — the Avatar Studio, for a parent only. The server refuses everyone else anyway.
  const actorContext = useOptionalActor();
  const actor = actorContext?.actor ?? null;
  const isParent = actor?.isAdmin === true;
  const studioLink = useMemo(() => studioLinkFromHash(), []);
  const [studioMembers, setStudioMembers] = useState({ status: 'idle', members: [] });
  useEffect(() => {
    if (!isParent) { setStudioMembers({ status: 'idle', members: [] }); return undefined; }
    let live = true;
    setStudioMembers({ status: 'loading', members: [] });
    fetchFamilyMembers().then(
      (rows) => { if (live) setStudioMembers({ status: 'ready', members: toStudioMembers(rows) }); },
      () => { if (live) setStudioMembers({ status: 'unavailable', members: [] }); },
    );
    return () => { live = false; };
  }, [isParent, actor?.tenantId]);

  /**
   * The switch values on screen, and the note under them.
   *
   * Held HERE rather than left to the component, because a switch must not keep a value the
   * server refused. The flip is optimistic so the control answers at once, and a rejected save
   * puts it back — which is only possible while this container owns the map.
   */
  const [toggleValues, setToggleValues] = useState(null);
  const [saveNote, setSaveNote] = useState('');

  /**
   * The last map the SERVER confirmed, and a queue that keeps writes in order.
   *
   * Both exist for one defect. The first version captured the whole toggle map before each write
   * and restored that capture on failure. Flip approval, then flip weekend grace before the first
   * PATCH settles, and the second capture already holds the first flip's OPTIMISTIC value — so
   * when both are refused, the second rollback puts approval back ON. Two refused writes, and the
   * screen ends up claiming a setting the server never accepted. A cross-model reviewer found it.
   *
   * The queue makes settlement order match click order. The confirmed map makes a rollback restore
   * ONE field to what the server last actually said, never a whole snapshot of hopes.
   */
  const confirmedRef = useRef(null);
  const queueRef = useRef(Promise.resolve());

  /** Run `job` after every write already queued, whether those succeeded or not. */
  const enqueue = useCallback((job) => {
    const next = queueRef.current.then(job, job);
    // Swallow on the CHAIN only. `next` keeps its rejection for the caller that awaits it.
    queueRef.current = next.then(() => undefined, () => undefined);
    return next;
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    let live = true;
    setState((prev) => ({ ...prev, status: 'loading', error: null }));

    // The profile is the only REQUIRED read — it carries the identity. The family name is
    // decoration, so a failure there leaves the component's own generic band title standing
    // rather than failing a page a family came here to use.
    api.get('/api/auth/profile', { signal: controller.signal })
      .then(async (profile) => {
        // **A failed read is remembered, not just absent.** The first version turned every
        // rejection into `null` and reported a clean ready state, so the switches vanished with
        // no reason given and no way back. A parent could not tell "this family has no settings"
        // from "we could not reach the server". A cross-model reviewer found it.
        let tenant = null;
        let tenantFailed = false;
        try {
          tenant = await fetchTenant();
          if (tenant === null) tenantFailed = true;
        } catch {
          tenantFailed = true;
        }
        if (!live || controller.signal.aborted) return;
        setState({ status: 'ready', profile, tenant, tenantFailed, error: null });
        // `null` when the family could not be read, which is what hides the switches. Defaulting
        // them here would state four settings about a family this screen never reached.
        const values = toToggleValues(tenant);
        confirmedRef.current = values;
        setToggleValues(values);
        setSaveNote('');
      })
      .catch((error) => {
        if (!live || controller.signal.aborted) return;
        setState({ status: 'error', profile: null, tenant: null, tenantFailed: true, error });
        confirmedRef.current = null;
        setToggleValues(null);
      });

    return () => { live = false; controller.abort(); };
  }, [reloadKey]);

  const reload = useCallback(() => setReloadKey((n) => n + 1), []);

  const { profile, tenant, status } = state;
  const ready = status === 'ready';
  const familyName = typeof tenant?.name === 'string' ? tenant.name : '';
  const name = useMemo(() => displayNameOf(profile), [profile]);
  const profileRows = useMemo(() => toProfileRows(profile), [profile]);
  const errorState = status === 'error' ? errorStateFor(state.error) : null;

  const payday = useMemo(() => paydayNameFor(tenant), [tenant]);

  /**
   * The one line under the family switches.
   *
   * A save result wins, because it is what the parent just did. Otherwise the standing state is
   * reported: the family could not be read at all, or it was read and its payday cannot be.
   * Saying nothing is what made a vanished row indistinguishable from a family with no settings.
   */
  const familyNote = useMemo(() => {
    if (saveNote) return saveNote;
    if (state.tenantFailed) return FAMILY_UNREADABLE;
    if (tenant && payday.kind === 'unreadable') return PAYDAY_UNREADABLE;
    return '';
  }, [saveNote, state.tenantFailed, tenant, payday.kind]);

  /**
   * One PATCH carrying one field, and the switch goes back if the server refuses.
   *
   * `Settings.jsx` calls this as `(map, id, next)`. Only `id` and `next` are used: `map` is the
   * component's own merged view, and sending the whole map would re-assert two settings the
   * parent did not touch on every single flip.
   */
  const onTogglesChange = useCallback(async (_map, id, next) => {
    const field = TOGGLE_FIELD[id];
    // A switch with no column cannot be saved, and pretending otherwise is the defect this
    // screen's own header describes. There is no such switch today; this refuses the next one.
    if (!field) return;
    setSaveNote('');
    setToggleValues((prev) => ({ ...(prev || {}), [id]: next }));
    return enqueue(async () => {
      try {
        const saved = await updateTenant({ [field]: next });
        const values = toToggleValues(saved);
        // The SERVER's answer, never the value just sent. A clamped or refused setting must not
        // be shown as saved. A partial answer gives `null`, and that hides the switches rather
        // than inventing three.
        setState((prev) => ({ ...prev, tenant: saved }));
        confirmedRef.current = values;
        setToggleValues(values);
        if (values === null) setSaveNote(FAMILY_UNREADABLE);
        // A SETTLED success says so, in the same place a settled failure says so. Nothing else on
        // this screen can tell the two apart at the moment they happen: `setToggleValues` above
        // ALSO runs optimistically before the request, so a watcher keyed on the toggle values
        // cannot know whether an answer has arrived. #699 shipped exactly that watcher and a
        // REFUSED save announced "is saved: off" on dev — the opposite of the truth. The note is
        // the only signal set once, after the await, per outcome.
        else setSaveNote(savedNoteFor(id, values[id]));
      } catch (error) {
        // ONE field, back to what the server last confirmed. Restoring a whole captured map is
        // what let a second refusal resurrect a first refusal's optimistic value.
        setToggleValues((prev) => {
          if (!prev) return prev;
          const confirmed = confirmedRef.current;
          if (!confirmed || typeof confirmed[id] !== 'boolean') return prev;
          return { ...prev, [id]: confirmed[id] };
        });
        setSaveNote(saveNoteFor(error));
      }
    });
  }, [enqueue]);

  // #580 4a3: the change warning. Only a parent is probed; everyone else keeps today's screen.
  // A committed change publishes the SERVER's new setting from its checked receipt.
  const onChangeCommitted = useCallback((period) => {
    setState((prev) => ({ ...prev, tenant: prev.tenant ? { ...prev.tenant, paydayPeriod: period } : prev.tenant }));
  }, []);
  const change = usePaydayChange({
    tenantId: isParent ? actor?.tenantId : undefined,
    actorId: actor?.id,
    savedPeriod: tenant?.paydayPeriod,
    onCommitted: onChangeCommitted,
    // The confirmation waits behind any switch save already sent, so an older tenant snapshot can
    // never land after the committed change and show the old payday.
    queue: enqueue,
  });
  const changeOn = change.mode === 'on';

  /** The payday, sent as the PERIOD the column holds — never the display name. */
  const onPaydayChange = useCallback(async (value) => {
    let period;
    try {
      // The change selector's values are periods; today's weekday select sends a day name.
      period = PAYDAY_PERIODS.includes(value) ? value : paydayPeriodFromDayName(value);
    } catch {
      // `payday.js` throws rather than guessing, and a guess here writes Sunday into a family's
      // account for an input that meant nothing.
      setSaveNote(SAVE_FAILED);
      return;
    }
    setSaveNote('');
    // Switched on: choosing opens the warning and saves nothing. `legacy` means the server said
    // the change flow is off, so it is today's PATCH after all.
    if ((changeOn || change.unresolved) && (await change.propose(period)) !== 'legacy') return;
    // Queued with the switches, so a payday save and a switch save cannot settle out of order and
    // publish an older tenant snapshot over a newer one.
    return enqueue(async () => {
      try {
        const saved = await updateTenant({ paydayPeriod: period });
        setState((prev) => ({ ...prev, tenant: saved }));
      } catch (error) {
        // Switched on since this screen looked: nothing was saved, and the warning opens instead.
        if (codeOf(error) !== CONFIRMATION_REQUIRED) {
          setSaveNote(saveNoteFor(error));
          return;
        }
        // `shown` and `refused` speak in the panel. `legacy` means the flow is off after all, so
        // today's PATCH is sent once more. Anything else says the save did not happen.
        const next = await change.escalate(period);
        if (next === 'shown' || next === 'refused') return;
        if (next !== 'legacy') {
          setSaveNote(saveNoteFor(error));
          return;
        }
        try {
          const saved = await updateTenant({ paydayPeriod: period });
          setState((prev) => ({ ...prev, tenant: saved }));
        } catch (again) {
          setSaveNote(saveNoteFor(again));
        }
      }
    });
  }, [change, changeOn, enqueue]);

  return (
    <Suspense fallback={<LanternLoader label="Loading your settings…" />}>
      <Settings
        showSnapshotNotice={false}
        profileName={ready ? name : ''}
        // The family's own name when the tenant answered. When it did not, the component's own
        // generic default ("Settings & profile") stands, because inventing a family name is the
        // defect being removed.
        {...(familyName ? { bandTitle: familyName } : {})}
        bandSubtitle={bandNoticeFor(persistence)}
        // #577: the live Studio for a parent. The old presentational section stays shut below.
        avatarStudio={isParent && studioMembers.status === 'unavailable' ? (
          <section className="set-card set-card--full" id="avatar-studio" aria-label="Avatar Studio">
            <h2>Avatar Studio</h2>
            <p role="alert">{STUDIO_UNAVAILABLE}</p>
          </section>
        ) : isParent && studioMembers.status === 'ready' && studioMembers.members.length > 0 ? (
          <Suspense fallback={<LanternLoader label="Opening the Avatar Studio…" />}>
            <AvatarStudio
              members={studioMembers.members}
              tenantSlug={actor?.tenantSlug}
              initialSubjectId={studioLink.avatarFor}
              googleAvailable={actor?.authProvider === 'google'}
              googleReturn={studioLink.googleReturn}
              scrollOnOpen={studioLink.open}
            />
          </Suspense>
        ) : null}
        // #577 L19, the spec's "one-tap clear (also reachable later from settings)". A parent only:
        // the server refuses everyone else, and the panel draws nothing for a family without examples.
        sampleWeek={isParent && actor?.tenantId ? (
          <SampleWeekPanel familyId={actor.tenantId} mode="settings" className="set-card set-card--full" headingLevel={2} />
        ) : null}
        // E24/E25 answer for the signed-in account. A child acting on that account, a display,
        // or an unresolved actor must never see the account holder's switches as their own.
        // A confirmed nobody-acting result remains the account session (d-20260928-21).
        notificationSettings={actor?.kind === 'parent' && !actor.actingUnknown && actorContext?.status !== 'unavailable' ? (
          <NotificationSettings key={`${actor.tenantId}:${actor.id}`} viewer={{ role: actor.role }} />
        ) : null}
        // The whole Avatar Studio, held shut by all three of its openers. See the header.
        currentAvatar={null}
        familyAvatars={[]}
        avatarSources={[]}
        profileRows={profileRows}
        // No endpoint carries a count. The fixture's 3 was invented.
        notifications={0}
        // #577 line 29 part 3. The switches are drawn ONLY for a family this screen actually
        // read. `toggleValues` is null when `GET /api/tenants/current` failed, and passing
        // `toggles={[]}` then is what keeps the component's own three defaults off a screen that
        // knows nothing about this family.
        //
        // When it IS null the component's `toggles` default would still supply three rows, so the
        // empty array has to be passed explicitly — omitting the prop is not the same thing.
        {...(toggleValues ? { toggleValues } : { toggles: [] })}
        onTogglesChange={onTogglesChange}
        // Monday-first display names. `payday.js` owns every mapping between this list, the
        // `Date.getDay()` numbers onboarding emits, and the `weekly_*` period the column holds.
        //
        // The row is drawn only for a family that settles up on a WEEKDAY. A select whose value
        // matches no option renders as the FIRST option, so drawing it for a monthly family would
        // tell them they are paid on Monday.
        //
        // Monthly, ad hoc (#580) and unreadable all hide it, but they are NOT the same answer and
        // no longer read as one: `calendar_month` and `ad_hoc` are real choices with no weekday and
        // say nothing, while a period this build cannot read says so in `familyNote`.
        //
        // #580 4a3: with the change flow switched on, every family gets all three types, and the
        // select keeps showing the SAVED value until the server commits a change.
        {...(changeOn && payday.kind !== 'unreadable'
          ? { paydayOptions: PAYDAY_CHOICES, paydayLabel: 'Payday', payday: tenant.paydayPeriod, paydayDescription: 'Choosing shows what the change would pay before anything is saved' }
          : payday.kind === 'weekly'
            ? { paydayOptions: WEEKDAY_NAMES_MONDAY_FIRST, payday: payday.name }
            : { paydayOptions: [] })}
        onPaydayChange={onPaydayChange}
        paydayChange={<PaydayChangePanel change={change} />}
        familySettingsNote={familyNote}
        // Offered only when there is something to retry. A button that re-reads a family already
        // read would be a control that does nothing.
        {...(state.tenantFailed && status === 'ready' ? { onFamilySettingsRetry: reload } : {})}
        // Unreadable records. See the table in the header.
        consentRows={[]}
        reconsentNeeded={false}
        dangerActions={[]}
        loading={status === 'loading'}
        error={errorState ? errorState.message : null}
        {...(errorState?.title ? { errorTitle: errorState.title } : {})}
        {...(!errorState || errorState.retryable ? { onRetry: reload } : {})}
      />
    </Suspense>
  );
}
